import type React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Divider, Fleuron } from "./Ornament";
import { easeManor } from "../constants/constants";

type Tea = {
  name: string;
  type: string;
  description: string;
  tastingNotes: string;
  ingredients: string;
};

type Section = {
  title: string;
  slug: string;
  epigraph: string;
  teas: Tea[];
};

const sections: Section[] = [
  {
    title: "Black Tea",
    slug: "black",
    epigraph: "Full-bodied, warming, the backbone of the afternoon.",
    teas: [
      {
        name: "Celebration",
        type: "Flavored Black",
        description:
          "A blend of traditional English flavors built for gatherings of friends and family, capturing the magic of the seasons — a limited edition to savor anytime, or to gift to someone you love.",
        tastingNotes: "Apricot, hazelnut, cinnamon, and spice.",
        ingredients:
          "Black tea, apricot pieces, passion plum flavor, apricot flavor, blue cornflowers, hazelnut flavor, cinnamon spice flavor. Contains natural flavors.",
      },
      {
        name: "Chocolate Chai Supreme",
        type: "Flavored Black",
        description:
          "A classic chai base of Chinese and Indian black teas, layered with ginger, cardamom, cinnamon and nutmeg, then finished with natural chocolate and vanilla for a one-of-a-kind cup.",
        tastingNotes: "Chocolate, cardamom, and vanilla.",
        ingredients:
          "Black tea, ginger root, chocolate flavor, cardamom seeds, cardamom flavor, ground cinnamon, nutmeg, cardamom pods, cinnamon chips, vanilla flavor and cinnamon flavor. Contains natural flavors.",
      },
      {
        name: "Holiday",
        type: "Flavored Black",
        description:
          "Spiced with citrus, almond, clove, and cinnamon. A year-round favorite — delicious hot or iced, lovely poured piping on a cold morning.",
        tastingNotes: "Orange and cloves with hints of vanilla and almond.",
        ingredients:
          "Black tea, cloves, almond flavor, orange flavor, vanilla flavor, safflowers, three types of cinnamon, orange pieces. Contains natural flavors.",
      },
      {
        name: "Hot Apple Spice",
        type: "Flavored Black",
        description:
          "A bright autumnal blend bursting with orchard-ripe apples alongside cinnamon, orange peel, and ground cloves — sweet and warming with just the right measure of spice.",
        tastingNotes: "Spicy, sweet apples.",
        ingredients:
          "Black tea, orange peel, cinnamon flavor, ground cinnamon, apple pieces, apple flavor, cinnamon pieces, ground cloves. Contains natural flavors.",
      },
      {
        name: "Lapsang Souchong",
        type: "Black",
        description:
          "An ancient and much-loved tea from the Wuyi Mountains of Fujian, China — where black tea was first developed. Slowly withered and dried over pine smoke, it carries a character unlike any other cup.",
        tastingNotes: "Pine smoke with some dark stone fruit.",
        ingredients: "Black tea.",
      },
      {
        name: "Nutcracker",
        type: "Flavored Black",
        description:
          "A medium-bodied black tea dressed in chocolate and nut flavors — a festive blend that conjures home, family, and the holidays.",
        tastingNotes:
          "Chocolate, caramel, and nut flavors come together into a beautifully sweet blend.",
        ingredients:
          "Black tea, chocolate flavor, vanilla flavor, almond flavor, hazelnut flavor, apricot pieces, apricot flavor, marigold petals. Contains natural flavors; does not contain nuts.",
      },
      {
        name: "Orange Pekoe",
        type: "Black",
        description:
          "A classic, satisfying blend of strong Assam and smooth-liquor Ceylon. Made from small, broken (BOP) leaves in the British tradition — the mellow, comforting cup John Harney himself reached for.",
        tastingNotes: "The sweetness of soaked grains.",
        ingredients: "Black tea.",
      },
      {
        name: "Paris",
        type: "Flavored Black",
        description:
          "Mike Harney's homage to the Parisian tea shops he loves — a fruity black tea with vanilla and caramel flavors and a whisper of lemony bergamot.",
        tastingNotes:
          "Sweet like caramel with the fruit of black currants and a hint of vanilla.",
        ingredients:
          "Black tea, oolong tea, black currant flavor, vanilla flavor, bergamot oil, caramel flavor.",
      },
      {
        name: "Queen Catherine",
        type: "Black",
        description:
          "A signature Harney & Sons blend of three Chinese black teas — Keemun, Panyang, and Yunnan — in honor of Queen Catherine of Braganza, who introduced her love of tea to the British. Featured in the Museum of Tea in Hangzhou.",
        tastingNotes:
          "Round and relaxed, a blend of dark honey, unsweetened cocoa, and hints of spice.",
        ingredients: "Black tea.",
      },
    ],
  },
  {
    title: "Green Tea",
    slug: "green",
    epigraph: "Grassy, delicate, a little sun in the cup.",
    teas: [
      {
        name: "Dragon Pearl Jasmine",
        type: "Green",
        description:
          "A masterpiece from Fuan, China — small hand-rolled pearls gently infused with jasmine flowers. Floral, sweet, light in color, pure joy in the pot.",
        tastingNotes:
          "Sweet and very floral. Please enjoy it as is; there is no need to alter it at all.",
        ingredients: "Green tea, white tea.",
      },
      {
        name: "Genmaicha",
        type: "Green",
        description:
          "Brown rice kernels (genmai) are added while the green Bancha leaves (cha) are drying — the kernels crisp and some burst open, giving this cup its unique appearance and roasted character.",
        tastingNotes:
          "A baseline of spring grass under a strong roasted note from the toasted rice — evocative of popcorn.",
        ingredients: "Green tea, toasted brown rice.",
      },
      {
        name: "Hojicha",
        type: "Green",
        description:
          "A creative Japanese use of tea by-products — once the best leaves became Sencha and the larger leaves Yanagi, the stems became Hojicha. For us, it is simply roasted twigs.",
        tastingNotes: "Roasted coffee.",
        ingredients: "Roasted tea stalks and twigs.",
      },
    ],
  },
  {
    title: "Oolong Tea",
    slug: "oolong",
    epigraph: "Between black and green — complex, layered, forgiving.",
    teas: [
      {
        name: "Formosa Oolong",
        type: "Oolong",
        description:
          "The style of brown oolong that generations of Americans have loved, especially for its toasty flavor. Don't worry about the stems — the tea is traditionally made this way in Taiwan.",
        tastingNotes:
          "Subtle, sweet peaches and roasted carrots beneath toasted walnut.",
        ingredients: "Oolong tea.",
      },
      {
        name: "Milky Oolong",
        type: "Oolong",
        description:
          "Light-bodied, smooth, creamy — deep green rolled leaves unfurl to a pale liquor reminiscent of gently steamed milk. A silky, mellow experience from first sip to last.",
        tastingNotes: "The sweetness of warmed milk and cream.",
        ingredients:
          "Oolong tea, milk flavor. Contains natural ingredients. Dairy-free.",
      },
    ],
  },
  {
    title: "Herbal Tea",
    slug: "herbal",
    epigraph: "Caffeine-free infusions for the unhurried hour.",
    teas: [
      {
        name: "Chamomile",
        type: "Herbal",
        description:
          "The finest Egyptian chamomile flowers — a premium, light-bodied herbal for winding down at the end of a long afternoon.",
        tastingNotes: "Very subtle apple flavors in a mellow cup.",
        ingredients: "Egyptian chamomile.",
      },
      {
        name: "Coconut Mint",
        type: "Herbal",
        description:
          "A spirited, caffeine-free infusion bringing the zest of organic lemongrass and the warmth of ginger root together with spearmint and coconut. Creamy, minty, and sugar-free.",
        tastingNotes: "Lemony mint mixed with coconut and other spices.",
        ingredients:
          "Organic lemongrass, organic ginger root, organic spearmint, organic coconut pieces, organic fennel seeds, organic coconut flavor, organic vanilla flavor.",
      },
      {
        name: "Organic Hibiscus Ginger",
        type: "Herbal",
        description:
          "An infusion designed to support your body's defenses — tropical hibiscus, rosehips, apple, and echinacea for a bright antioxidant base, with lemon peel, ginger, eucalyptus, peppermint, and a sparkle of clove.",
        tastingNotes: "Tart notes of ginger and hibiscus.",
        ingredients:
          "Organic hibiscus, organic lemon myrtle, organic eucalyptus, organic ginger, organic echinacea, organic lemon peel, organic cloves, organic rosehips, organic peppermint, organic apple, organic beet root.",
      },
      {
        name: "Peppermint",
        type: "Herbal",
        description:
          "An organic version of the peppermint herbal we know and love — refreshing and brisk, delicious hot or iced. Sourced from the state of Oregon.",
        tastingNotes: "Clean and refreshing peppermint.",
        ingredients: "Organic peppermint.",
      },
    ],
  },
  {
    title: "Fruit Tea",
    slug: "fruit",
    epigraph: "Bright, fragrant, and uncaffeinated.",
    teas: [
      {
        name: "Blood Orange",
        type: "Fruit",
        description:
          "A brilliant blend of dried fruit with the distinctive twist of blood orange. Delicious hot or cold, and it brews to a vivid orange. Caffeine-free.",
        tastingNotes: "Blood orange, bright and juicy.",
        ingredients:
          "Apple, rosehips, hibiscus, beet root, orange peel, natural orange flavor, marigold, safflowers.",
      },
    ],
  },
];

const TeaEntry: React.FC<{ tea: Tea; idx: number; last: boolean }> = ({
  tea,
  idx,
  last,
}) => (
  <motion.article
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.9, ease: easeManor, delay: (idx % 4) * 0.05 }}
  >
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-6">
      <h4 className="font-display text-3xl md:text-4xl text-[color:var(--claret-deep)] leading-tight">
        {tea.name}
      </h4>
      <span className="u-label">{tea.type}</span>
    </div>
    <p className="mt-3 italic text-[color:var(--claret)]">{tea.tastingNotes}</p>
    <p className="mt-3 text-[color:var(--ink-soft)] leading-relaxed">
      {tea.description}
    </p>
    <p className="mt-3 text-sm italic text-[color:var(--ink-soft)]/80">
      <span className="u-label mr-2">Ingredients</span>
      {tea.ingredients}
    </p>
    {!last && (
      <div className="mt-10 flex justify-center text-[color:var(--gilt-deep)]">
        <Fleuron className="w-10 h-3" />
      </div>
    )}
  </motion.article>
);

const SectionBlock: React.FC<{ section: Section }> = ({ section }) => (
  <section id={section.slug} className="scroll-mt-28">
    <div className="text-center">
      <Divider />
      <p className="mt-8 u-label">The House Pours</p>
      <h2 className="mt-3 font-display text-[clamp(2.6rem,6vw,4.25rem)] leading-[1] text-[color:var(--claret-deep)]">
        {section.title}
      </h2>
      <p className="mt-4 italic text-[color:var(--ink-soft)]">
        {section.epigraph}
      </p>
    </div>

    <div className="mt-14 max-w-2xl mx-auto space-y-14">
      {section.teas.map((t, i) => (
        <TeaEntry
          key={t.name}
          tea={t}
          idx={i}
          last={i === section.teas.length - 1}
        />
      ))}
    </div>
  </section>
);

const Menu: React.FC = () => {
  return (
    <div className="pt-28 pb-24 px-6">
      <motion.header
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: easeManor }}
        className="max-w-3xl mx-auto text-center"
      >
        <p className="u-label">The Afternoon</p>
        <h1 className="mt-4 font-display text-[clamp(2.8rem,7vw,5.25rem)] leading-[0.98]">
          The <em>Tea</em> Menu.
        </h1>
        <div className="mt-5">
          <span className="u-script" style={{ fontSize: "2.4rem" }}>
            a moment of warmth, pause, and gentle luxury
          </span>
        </div>
      </motion.header>

      <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: easeManor }}
        className="card-parchment relative max-w-3xl mx-auto mt-14 px-8 md:px-12 py-12 rounded-sm"
      >
        <div className="absolute inset-3 border border-[color:var(--gilt-hairline)] pointer-events-none" />
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-[color:var(--gilt-deep)]">
          <Fleuron className="w-12 h-3" />
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[color:var(--gilt-deep)]">
          <Fleuron className="w-12 h-3" />
        </div>
        <p className="text-lg leading-[1.8] text-[color:var(--ink-soft)] text-center">
          Tea is more than a beverage — it is a moment of warmth, pause, and
          gentle luxury. Each cup invites you to slow down, breathe deeply, and
          savor something beautifully simple. Whether you reach for a fragrant
          floral, a cozy holiday blend, or a bright herbal infusion, may this
          menu guide you to the tea that speaks to your spirit today.
        </p>
        <p className="mt-6 italic text-center text-[color:var(--claret)]">
          May every sip bring you comfort, connection, and a touch of wonder.
        </p>
      </motion.div>

      <nav className="max-w-3xl mx-auto mt-16 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
        {sections.map((s) => (
          <a
            key={s.slug}
            href={`#${s.slug}`}
            className="font-smallcaps tracking-ceremonial uppercase text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--claret-deep)] transition-colors duration-300"
          >
            {s.title}
          </a>
        ))}
      </nav>

      <div className="max-w-5xl mx-auto mt-24 space-y-28">
        {sections.map((s) => (
          <SectionBlock key={s.slug} section={s} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: easeManor }}
        className="max-w-3xl mx-auto mt-24 text-center"
      >
        <Divider />
        <p className="mt-8 u-label">
          Served with scones, finger sandwiches, and pâtisserie
        </p>
        <h3 className="mt-4 font-display text-3xl md:text-4xl text-[color:var(--claret-deep)]">
          By <em>reservation</em>, as ever.
        </h3>
        <div className="mt-8">
          <Link to="/contact" className="btn-seal">
            Begin a Reservation
          </Link>
        </div>
        <Divider className="mt-14" />
      </motion.div>
    </div>
  );
};

export default Menu;
